// The adaptation studio: dials on the left, the lesson being rewritten on the right.
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { getCatalogLesson, type Lesson } from "../../shared/catalog";
import { describeParams, docFromAdaptation, docFromLesson, paramsFromLesson } from "../../shared/lesson-model";
import { CEFR } from "../../shared/catalog";
import { MODES, TECH } from "../../shared/constants";
import { api, ApiError, streamAdapt } from "../lib/api";
import { Link, useLocation } from "../lib/router";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import type { Adaptation, AdaptParams, Profile } from "../lib/types";
import { Dials } from "../components/Dials";
import { Kit, LessonDocView, Ledger } from "../components/LessonDocView";
import { CompareView } from "../components/CompareView";
import { AdaptationActions } from "../components/AdaptationActions";
import { LimitNotice } from "../components/LimitNotice";
import { StageStrip } from "../components/StageStrip";
import { Icon } from "../components/Icon";
import { Paywall } from "../components/Paywall";
import { pageTitle } from "../brand";

const ACTIVE_PROFILE = "las.activeProfile";
type Tab = "lesson" | "compare" | "changes" | "kit";

function phaseOf(text: string, kit: boolean): [string, number] {
  const expected = kit ? 7200 : 4600;
  const pct = Math.min(0.97, text.length / expected);
  if (text.includes('"kit"')) return ["Writing the printable materials", pct];
  if (text.includes('"ledger"')) return ["Noting what changed and why", pct];
  if (text.includes('"differentiation"')) return ["Adding support and stretch", pct];
  if (text.includes('"stages"')) return ["Re-timing and rewriting each stage", pct];
  if (text.includes('"objective"')) return ["Rewriting the objective for this level", pct];
  return ["Reading the original lesson", pct];
}

function paramsFromQuery(base: AdaptParams, q: URLSearchParams): AdaptParams {
  const p = { ...base };
  const lv = q.get("level");
  if (lv && (CEFR as readonly string[]).includes(lv)) p.level = lv as AdaptParams["level"];
  const d = Number(q.get("duration"));
  if (d >= 10 && d <= 120) p.duration = Math.round(d / 5) * 5;
  const cs = Number(q.get("classSize"));
  if (cs >= 1 && cs <= 80) p.classSize = cs;
  const t = q.get("tech");
  if (t && (TECH as readonly string[]).includes(t)) p.tech = t as AdaptParams["tech"];
  const m = q.get("mode");
  if (m && (MODES as readonly string[]).includes(m)) p.mode = m as AdaptParams["mode"];
  return p;
}

export default function Studio({ id }: { id: string }) {
  const lesson = getCatalogLesson(id);
  const { search } = useLocation();
  const { me, refresh } = useSession();
  const toast = useToast();
  const [params, setParams] = useState<AdaptParams>(() => (lesson ? paramsFromQuery(paramsFromLesson(lesson), search) : paramsFromLesson(getCatalogLesson("A1")!)));
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [profileId, setProfileId] = useState<string>("");
  const [result, setResult] = useState<Adaptation | null>(null);
  const [streamText, setStreamText] = useState("");
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState<{ cached: boolean; model: string } | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<Tab>("lesson");
  const [naming, setNaming] = useState(false);
  const [full, setFull] = useState<Lesson | null>(null);
  const [locked, setLocked] = useState(false);
  const [profileName, setProfileName] = useState("");
  const abort = useRef<AbortController | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Load class profiles and pre-apply the last one used (unless the URL carries explicit settings).
  useEffect(() => {
    if (!me?.user) return;
    api.profiles().then(({ profiles }) => {
      setProfiles(profiles);
      const saved = localStorage.getItem(ACTIVE_PROFILE);
      const p = profiles.find((x) => x.id === saved);
      if (p && lesson && ![...search.keys()].length) {
        setProfileId(p.id);
        setParams((cur) => ({ ...cur, ...p.params, includeKit: cur.includeKit }));
      }
    }).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user?.id]);

  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => {
    if (!lesson) return;
    setLocked(false);
    api.lesson(lesson.id).then((r) => setFull(r.lesson)).catch((e: ApiError) => {
      if (e.code === "NO_ACCESS" || e.code === "SIGN_IN_REQUIRED") setLocked(true);
    });
  }, [lesson, me?.access.state]);
  useEffect(() => {
    if (lesson) document.title = pageTitle(`Adapt ${lesson.id}`);
  }, [lesson]);

  const adaptedDoc = useMemo(() => (lesson && result ? docFromAdaptation(lesson, result.output) : null), [lesson, result]);

  if (!lesson) {
    return <div className="page narrow empty"><h1>No lesson called “{id}”</h1><p><Link to="/">Pick a lesson to adapt</Link>.</p></div>;
  }
  const kitLocked = false; // the Studio is only usable with an active trial or subscription, which includes kits
  const remaining = me?.usage.adapt.remaining;

  const applyProfile = (pid: string) => {
    setProfileId(pid);
    const p = profiles.find((x) => x.id === pid);
    if (p) {
      setParams({ ...p.params, duration: params.duration, includeKit: params.includeKit });
      localStorage.setItem(ACTIVE_PROFILE, pid);
    } else localStorage.removeItem(ACTIVE_PROFILE);
  };

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    const name = profileName.trim();
    if (!name) return;
    try {
      const { includeKit: _k, ...rest } = params;
      const { profile } = await api.createProfile(name, rest);
      setNaming(false);
      setProfileName("");
      setProfiles((xs) => [profile, ...xs]);
      setProfileId(profile.id);
      localStorage.setItem(ACTIVE_PROFILE, profile.id);
      toast(`Saved class profile “${profile.name}”`);
    } catch (e) {
      toast((e as ApiError).message, "error");
    }
  };

  const generate = async () => {
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    setBusy(true);
    setError(null);
    setStreamText("");
    setMeta(null);
    setResult(null);
    setTab("lesson");
    resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    try {
      const r = await streamAdapt(lesson.id, params, {
        signal: ctl.signal,
        onMeta: setMeta,
        onDelta: (t) => setStreamText((s) => s + t),
      });
      setResult(r.adaptation);
      void refresh();
    } catch (e) {
      if ((e as Error).name !== "AbortError") setError(e instanceof ApiError ? e : new ApiError(0, "UNKNOWN", (e as Error).message));
    } finally {
      setBusy(false);
    }
  };

  const [phase, pct] = phaseOf(streamText, params.includeKit);
  const original = full ? docFromLesson(full, params.duration) : null;

  if (locked || (me && !me.access.active && !full)) {
    return (
      <div className="page narrow-wide">
        <nav className="crumbs" aria-label="Breadcrumb"><Link to="/">Lessons</Link> <span aria-hidden="true">/</span> {lesson.id}</nav>
        <h1 className="studio-title"><span className="code">{lesson.id}</span> {lesson.activity}</h1>
        <p className="muted">{lesson.topic} · written for {lesson.level}, {lesson.durationLabel}</p>
        <Paywall what="the AI Adaptation Studio" />
      </div>
    );
  }
  return (
    <div className="page studio">
      <section className="studio-controls" aria-labelledby="studio-h">
        <nav className="crumbs" aria-label="Breadcrumb">
          <Link to="/">Lessons</Link> <span aria-hidden="true">/</span> <Link to={`/lesson/${lesson.id}`}>{lesson.id}</Link>
        </nav>
        <h1 id="studio-h" className="studio-title">
          <span className="code">{lesson.id}</span> {lesson.activity}
        </h1>
        <p className="muted">{lesson.topic} · written for {lesson.level}, {lesson.durationLabel}</p>

        {me?.user && (
          <div className="profile-picker">
            <label className="dial-label" htmlFor="prof">Class profile</label>
            <div className="inline-form">
              <select id="prof" value={profileId} onChange={(e) => applyProfile(e.target.value)}>
                <option value="">Custom settings</option>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <button type="button" className="btn btn-quiet" onClick={() => setNaming((n) => !n)} aria-expanded={naming}>Save as profile</button>
            </div>
            {naming && (
              <form className="inline-form name-form" onSubmit={(e) => void saveProfile(e)}>
                <label className="visually-hidden" htmlFor="pname">Profile name</label>
                <input id="pname" autoFocus maxLength={60} placeholder="e.g. Form 3 Cempaka" value={profileName} onChange={(e) => setProfileName(e.target.value)} />
                <button className="btn btn-primary" disabled={!profileName.trim()}>Save</button>
              </form>
            )}
          </div>
        )}

        <Dials value={params} onChange={(v) => { setParams(v); setProfileId(""); }} disabled={busy} />

        <label className={`check kit-toggle${kitLocked ? " locked" : ""}`}>
          <input type="checkbox" checked={params.includeKit} disabled={busy || kitLocked} onChange={(e) => setParams({ ...params, includeKit: e.target.checked })} />
          <span>
            <b>Include a printable Materials Kit</b>
            <span className="muted small">The actual cards, sentence frames and answer key, ready to photocopy.</span>
          </span>
        </label>

        <div className="generate">
          <button className="btn btn-primary btn-lg" onClick={() => void generate()} disabled={busy}>
            <Icon name="spark" /> {busy ? "Adapting…" : result ? "Adapt again with these settings" : "Adapt this lesson"}
          </button>
          {busy && <button className="btn btn-quiet" onClick={() => abort.current?.abort()}>Stop</button>}
          {typeof remaining === "number" && (
            <p className="muted small">
              {remaining} of {me!.usage.adapt.limit} adaptations left today. Repeating the same settings is free.
            </p>
          )}
        </div>
      </section>

      <section className="studio-result" ref={resultRef} aria-live="polite" aria-busy={busy}>
        {error && <LimitNotice error={error} onSignedIn={() => void refresh()} />}

        {busy && (
          <div className="progress-card">
            <p className="progress-phase">{meta?.cached ? "Found an identical adaptation" : phase}</p>
            <div className="progress-track"><span style={{ width: `${Math.round((meta?.cached ? 1 : pct) * 100)}%` }} /></div>
            <p className="muted small">{describeParams(params)}</p>
            <div className="ghost-doc" aria-hidden="true">
              {original && <StageStrip stages={original.stages} size="lg" />}
              <p className="muted small">Original timeline at {params.duration} minutes. The AI is reshaping it for your class.</p>
            </div>
          </div>
        )}

        {!busy && !result && !error && (
          <div className="studio-empty">
            <p className="empty-lead">Set the dials for your class, then adapt. You'll get the rewritten lesson, a side-by-side comparison with the original, and a note of every change tied to the handbook's principles.</p>
            {original && <LessonDocView doc={original} compact />}
          </div>
        )}

        {result && adaptedDoc && (
          <>
            <div className="result-head">
              <p className="muted small">
                {meta?.cached ? "Reused an identical adaptation (didn't use your allowance)." : `Adapted by ${result.model}.`} Check it before you teach it.
              </p>
              <AdaptationActions a={result} onChange={setResult} />
            </div>
            <div className="tabs" role="tablist" aria-label="Result views">
              {([["lesson", "Adapted lesson"], ["compare", "Side by side"], ["changes", `What changed (${adaptedDoc.ledger?.length ?? 0})`], ...(adaptedDoc.kit ? [["kit", "Materials kit"]] : [])] as [Tab, string][]).map(([k, label]) => (
                <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? "on" : ""} onClick={() => setTab(k)}>{label}</button>
              ))}
            </div>
            <div role="tabpanel">
              {tab === "lesson" && <LessonDocView doc={adaptedDoc} />}
              {tab === "compare" && full && <CompareView lesson={full} adapted={adaptedDoc} />}
              {tab === "changes" && (adaptedDoc.ledger ? <Ledger items={adaptedDoc.ledger} /> : <p>No changes were recorded.</p>)}
              {tab === "kit" && adaptedDoc.kit && <Kit kit={adaptedDoc.kit} />}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
